import { create } from 'zustand';
import { User as FirebaseUser } from 'firebase/auth';
import { User } from '@/types/user';
import { authService } from '@/services/firebase/authService';

/**
 * 認証ストアの状態型定義
 */
interface AuthState {
  // 状態
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  error: string | null;

  // アクション
  setUser: (user: User | null) => void;
  setFirebaseUser: (firebaseUser: FirebaseUser | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  initAuth: () => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * 認証ストア
 */
export const useAuthStore = create<AuthState>((set) => ({
  // 初期状態
  user: null,
  firebaseUser: null,
  loading: true,
  error: null,

  // アクション
  setUser: (user) => set({ user }),

  setFirebaseUser: (firebaseUser) => set({ firebaseUser }),

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error }),

  /**
   * 認証状態の初期化
   */
  initAuth: async () => {
    set({ loading: true, error: null });

    // 認証状態の監視を開始。コールバックは非同期で、内部の例外は
    // ここの try/catch では捕捉できないため、コールバック内で個別に守る必要がある。
    authService.onAuthStateChange(async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // ユーザー情報をFirestoreから取得
          const userProfile = await authService.getUserProfile(firebaseUser.uid);
          set({
            firebaseUser,
            user: userProfile,
            loading: false,
          });
        } else {
          set({
            firebaseUser: null,
            user: null,
            loading: false,
          });
        }
      } catch (error) {
        // 認証コールバック内で例外を握りつぶすと loading が永遠に true のまま固まり、
        // 画面が真っ白になる（App Check 拒否などで発生）。必ず loading: false を解放する。
        console.error('認証コールバックエラー:', error);
        set({
          firebaseUser,
          user: null,
          loading: false,
          error: error instanceof Error ? error.message : '認証情報の取得に失敗しました',
        });
      }
    });
  },

  /**
   * ログアウト
   */
  logout: async () => {
    try {
      await authService.logout();
      set({
        user: null,
        firebaseUser: null,
        error: null,
      });
    } catch (error) {
      console.error('ログアウトエラー:', error);
      set({
        error: error instanceof Error ? error.message : 'ログアウトに失敗しました',
      });
    }
  },
}));
